import { NotificationSeverity } from '@kc-console/context';
import {
  IcErrorCircleRegular,
  IcNoticeRegular,
  IcSuccessCircleRegular,
  IcWarningTriangleRegular,
} from '@kep/kcds-icons';

import type { ComponentType } from 'react';
import type { Theme } from '@mui/material';

/** 디자인 토큰 색상 키 (theme.color의 nested leaf). */
export type ColorKey = keyof Theme['color'];

// ---------------------------------------------------------------------------
// Severity-based maps (private)
// ---------------------------------------------------------------------------

const ICONS: Record<NotificationSeverity, ComponentType<{ height?: number; width?: number }>> = {
  [NotificationSeverity.SUCCESS]: IcSuccessCircleRegular,
  [NotificationSeverity.ERROR]: IcErrorCircleRegular,
  [NotificationSeverity.WARNING]: IcWarningTriangleRegular,
  [NotificationSeverity.INFO]: IcNoticeRegular,
};

const ICON_COLOR_UNREAD: Record<NotificationSeverity, ColorKey> = {
  [NotificationSeverity.SUCCESS]: 'icon/success/default',
  [NotificationSeverity.ERROR]: 'icon/danger/default',
  [NotificationSeverity.WARNING]: 'icon/warning/default',
  [NotificationSeverity.INFO]: 'icon/informative/default',
};

const ICON_COLOR_READ: Record<NotificationSeverity, ColorKey> = {
  [NotificationSeverity.SUCCESS]: 'icon/success/weak/default',
  [NotificationSeverity.ERROR]: 'icon/danger/weak/default',
  [NotificationSeverity.WARNING]: 'icon/warning/weak/default',
  [NotificationSeverity.INFO]: 'icon/informative/weak/default',
};

const SURFACE_UNREAD: Record<NotificationSeverity, ColorKey> = {
  [NotificationSeverity.SUCCESS]: 'surface/success/weaker/default',
  [NotificationSeverity.ERROR]: 'surface/danger/weaker/default',
  [NotificationSeverity.WARNING]: 'surface/warning/weaker/default',
  [NotificationSeverity.INFO]: 'surface/informative/weaker/default',
};

const SURFACE_READ: Record<NotificationSeverity, ColorKey> = {
  [NotificationSeverity.SUCCESS]: 'surface/success/weakest/default',
  [NotificationSeverity.ERROR]: 'surface/danger/weakest/default',
  [NotificationSeverity.WARNING]: 'surface/warning/weakest/default',
  [NotificationSeverity.INFO]: 'surface/informative/weakest/default',
};

// ---------------------------------------------------------------------------
// Read-state text colors (private)
// ---------------------------------------------------------------------------

const TEXT_UNREAD = {
  category: 'text/weakest/default',
  title: 'text/default',
  description: 'text/default',
} as const satisfies Record<string, ColorKey>;

const TEXT_READ = {
  category: 'text/weaker/default',
  title: 'text/weaker/default',
  description: 'text/weaker/default',
} as const satisfies Record<string, ColorKey>;

// ---------------------------------------------------------------------------
// Public
// ---------------------------------------------------------------------------

/**
 * severity + read 상태로부터 카드의 시각 토큰을 한 번에 반환.
 * @description 읽음이면 아이콘은 weak, 아이콘 배경은 weakest, 텍스트는 모두 weaker로 dim.
 */
export const getCardStyle = (severity: NotificationSeverity, read: boolean) => ({
  Icon: ICONS[severity],
  iconColorKey: (read ? ICON_COLOR_READ : ICON_COLOR_UNREAD)[severity],
  surfaceKey: (read ? SURFACE_READ : SURFACE_UNREAD)[severity],
  textColors: read ? TEXT_READ : TEXT_UNREAD,
});
